// group-table.component.ts
import { Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import {
  TableConfig,
  PaginatorFetcher,
  TableComponent,
  PaginatorFilters,
  AlertType,
  Alert,
  AlertService,
  ModalComponent,
  ModalResult
} from '@michelin/theme';
import { Group } from './model/group';
import { Pagination } from './model/pagination.model';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { PaginatedGroupResponse } from './model/paginated-group.model';
import { Store } from '@ngxs/store';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { UserManagementService } from '../../services/user-management.service';
import { UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DealerState } from '../../../../shared/state/DealerState';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { GroupFilters } from './model/group-filters.model';
import { DatePipe } from '@angular/common';
import { UpdateCurrentPaginatedGroupsAction } from '../../../../shared/state/UserManagementState';
import { UtilityService } from '../../../../shared/services/utility.service';
import { ADMIN_GROUP_NAME, FEATURE_FLAGS } from 'src/app/shared/constants/constants';
import { FeatureflagsService } from 'src/app/shared/services/featureflags.service';
import { environment } from '../../../../../environments/environment';

declare const gtag: (
  type: string,
  name: string,
  options: {
    event_category: string;
    event_label: string;
    value: string;
  }
) => void;

@Component({
  selector: 'app-group',
  templateUrl: './group.component.html',
  styleUrls: ['./group.component.scss'],
  standalone: false
})
export class GroupComponent implements OnInit {
  public groupToDelete: Group;

  groups: Group[] = [];
  pagination: Pagination;
  selection: Group[] = [];
  pageSizeOptions = [1, 5, 10, 50, 100];
  currentPage = 1;
  pageSize = 10;
  dealer: Dealer;
  username: string;

  @ViewChild('deleteUserGroupModal', { static: true }) deleteUserGroupModal: ModalComponent;
  @ViewChild('deleteUserGroupModalWarning') deleteUserGroupContentModal: TemplateRef<any>;
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  public alertService = inject(AlertService);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public userManagementService = inject(UserManagementService);
  public formBuilder = inject(UntypedFormBuilder);
  public route = inject(ActivatedRoute);
  public datePipe = inject(DatePipe);
  public router = inject(Router);
  public featureflagsService = inject(FeatureflagsService);
  public utilityService = inject(UtilityService);

  groupTableConfig: TableConfig<Group> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    onRowClick: (group: Group) => {
      if (group.groupName?.toUpperCase() === ADMIN_GROUP_NAME) {
        return;
      }
      this.router.navigate(['home/setup/usersetup/user-group', group.groupName, group.userGroupId]);
    },
    onColumnsChange: (columns: string[]) => console.log('Column display change', columns),
    columns: {}
  };
  showTblHiddenColumns: boolean = false;

  getGroupFilters(): GroupFilters {
    const formValues = this.form.value;
    const groupFilters: GroupFilters = {
      groupName: formValues.groupName || null
    };
    return groupFilters;
  }

  groupFetcher: PaginatorFetcher<Group> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    const groupFilters = this.getGroupFilters();
    return this.userManagementService.getGroups(this.dealer.id, groupFilters, filters.page, filters.limit).pipe(
      map((groupResponse: PaginatedGroupResponse) => {
        if (groupResponse != null && groupResponse.error != null && groupResponse?.error?.length > 0) {
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('tryAgainText')
          } as Alert);
          return;
        } else {
          this.groups = groupResponse.data.groups.filter((group) => group.groupName?.toUpperCase() !== ADMIN_GROUP_NAME);
          this.pagination = groupResponse.data.pagination;
          this.store.dispatch(new UpdateCurrentPaginatedGroupsAction(this.groups));
          return {
            items: this.groups,
            count: this.pagination.totalRecords
          };
        }
      })
    );
  };

  state = TableConfig.getState(this.groupTableConfig, this.route);

  form = this.formBuilder.group({
    groupName: [this.state.extraFilters?.groupName]
  });

  constructor() {}

  ngOnInit(): void {
    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.form.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((values) => {
      this.tableRef.patchExtraFilters(values, true);
      if (values.groupName && values.groupName.trim() !== '') {
        this.trackUserSearch('name', values.groupName);
      }
    });
    this.showTblHiddenColumns = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.SHOW_USRTBL_HIDDEN_COLS);

    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.groupTableConfig.columns = {
        groupName: {
          cell: (elm: Group) => elm.groupName,
          header: this.translationService.translate('userGroupNameText'),
          sortable: false
        },
        description: {
          cell: (elm: Group) => elm.description,
          header: this.translationService.translate('descriptionText'),
          sortable: false
        },
        assignedTo: {
          cell: (elm: Group) => elm.assignedTo,
          header: this.translationService.translate('assignedToText'),
          sortable: true
        },
        lastModifiedOn: {
          cell: (elm: Group) => this.datePipe.transform(elm.lastUpdtTimestamp, 'yyyy-MM-dd'),
          header: this.translationService.translate('updatedOnColumnText'),
          sortable: false
        },
        lastModifiedBy: {
          cell: (elm: Group) => elm.lastModifiedBy,
          header: this.translationService.translate('lastModifiedByText'),
          sortable: false
        },
        action: {
          cell: 'actionCell',
          header: this.translationService.translate('actionText'),
          sortable: true
        }
      };
    });
    if (this.showTblHiddenColumns) {
      this.groupTableConfig = {
        ...this.groupTableConfig,
        columns: this.groupTableConfig.columns || {},
        onSelectionChange: (elms: Group[]) => console.log('Selection change', elms),
        selection: {
          columnKeyForCheckboxLabel: 'name',
          allLabel: 'All'
        }
      };
    }
  }

  resetFilters() {
    this.form.reset();
  }

  onActionClick(item: Group): void {
    if (item.groupName?.toUpperCase() === ADMIN_GROUP_NAME) {
      return;
    }
    if (item.assignedTo.valueOf() !== '0 users') {
      this.utilityService.showAlert(AlertType.critical, '', this.translationService.translate('cannotDeleteUserGroupText'));
      return;
    } else {
      this.groupToDelete = item;

      this.deleteUserGroupModal.open({
        id: 'delete-user-group-warning',
        width: 600,
        title: this.translationService.translate('warningText'),
        content: this.deleteUserGroupContentModal,
        confirm: this.translationService.translate('yesText'),
        cancel: this.translationService.translate('noText')
      });
    }
  }

  onDeleteUserGroupModalClosed(result: ModalResult) {
    if (result.result) {
      this.deleteUserGroup(this.groupToDelete);
    }
  }

  deleteUserGroup(group: Group): void {
    if (environment.envName !== 'dev') {
      // Sends an event to Google Analytics to track when a user group is deleted.
      // The event includes the dealer ID and details of the deleted user group.
      gtag('event', `delete_user_group`, {
        event_category: 'USER_GROUP_MANAGEMENT',
        event_label: `Delete User Group`,
        value: `Dealer: ${this.dealer.id}, Deleted User Group: ${this.groupToDelete}`
      });
    }
    // Call the delete API
    this.userManagementService.deleteGroup(this.dealer.id, group.userGroupId).subscribe({
      next: () => {
        let successMessage = 'User Group deleted successfully';
        const filters = { ...this.form.value, _refresh: Date.now(), page: this.currentPage };
        if (this.tableRef && this.tableRef.patchExtraFilters) {
          this.tableRef.patchExtraFilters(filters, true);
        }
        this.utilityService.showAlert(AlertType.success, '', successMessage);
      },
      error: () => {
        let errorMessage = 'User Group could not be deleted';
        this.utilityService.showAlert(AlertType.critical, '', errorMessage);
      }
    });
  }

  populateCreateUserGroup() {
    if (environment.envName !== 'dev') {
      // Sends an event to Google Analytics to track when the "Add User Group" button is clicked.
      gtag('event', `add_user_group`, {
        event_category: 'USER_GROUP_MANAGEMENT',
        event_label: `Add User Group`,
        value: `Add User Group Clicked`
      });
    }
    this.router.navigate(['home/setup/usersetup/user-group/create']);
  }

  // This method tracks user search actions and sends events to Google Analytics.
  // It logs the search field (e.g., name, user group) and the search value entered by the user.
  // The event helps monitor user behavior and search patterns in the application.
  private trackUserSearch(searchField: string, searchValue: string): void {
    if (environment.envName !== 'dev') {
      gtag('event', `search_group_by_${searchField}`, {
        event_category: 'USER_GROUP_MANAGEMENT',
        event_label: `Search by ${searchField}`,
        value: `Dealer: ${this.dealer.id}, ${searchField} search: ${searchValue}`
      });
    }
  }
}
